import { useEffect, useRef, type ReactNode } from "react";
import type { OrgSummary, SessionUser } from "@utuwrk/core";
import { mayOpen } from "../lib/access.js";
import { go, pathOf, type Screen } from "../lib/router.js";
import { useTheme, type Theme } from "../lib/theme.js";
import { Link } from "./bits.js";
import { Icon, type IconName } from "./Icon.js";
import { Menu, MenuItem, Segmented } from "@vcnet/ui";

/**
 * The frame every signed-in screen sits in, in utucat's shape.
 *
 * **Wide**, a sidebar: the lockup, Start onboarding for whoever may, the
 * places you work (My work, Notices, Onboarding, Offboarding, Employees), a
 * rule, the places you visit (People, Settings, Preview, Platform), and at the
 * foot the account menu. **A phone** gets a slim top bar with the bell and a
 * bottom tab bar: three screens, Start as a raised button in the middle where
 * a thumb already is, and More for the rest. One breakpoint (60rem), all CSS.
 *
 * Which screens are drawn follows the person's role, as a courtesy: every
 * route checks the role again in the worker, and a screen reached by typing
 * its address gets the worker's refusal in its own words.
 */

type Entry = { key: string; label: string; icon: IconName; to: Screen; on: boolean; count?: number };

const initialOf = (user: SessionUser): string =>
  (user.name || user.email).trim().charAt(0).toUpperCase() || "?";

function Wordmark() {
  return (
    <>
      <img src="/icon.svg" alt="" width={28} height={28} />
      <span className="wordmark" aria-label="utuwrk">
        utu<span className="wordmark-soft">wrk</span>
      </span>
    </>
  );
}

export function AppShell({
  user,
  organizations,
  org,
  onOrg,
  screen,
  unread,
  canStart,
  leaving,
  onSignOut,
  children,
}: {
  user: SessionUser;
  organizations: OrgSummary[];
  org: OrgSummary | null;
  onOrg: (id: string) => void;
  screen: Screen;
  /** Notices not yet read; null outside an organisation. */
  unread: number | null;
  canStart: boolean;
  /** A sign-out is under way. */
  leaving: boolean;
  onSignOut: (everywhere: boolean) => void;
  children: ReactNode;
}) {
  const [theme, setTheme] = useTheme();
  const mainRef = useRef<HTMLElement>(null);
  const role = org?.role ?? null;
  const may = (entry: Entry) => mayOpen(entry.to, role, user.isAdmin, org?.plan ?? null);
  const oversees = may({ to: { name: "onboardings" } } as Entry);
  const on = (...names: Screen["name"][]) => names.includes(screen.name);
  const offboarding = "kind" in screen && screen.kind === "offboarding";

  // Every screen after the first is a client-side swap, invisible to a screen
  // reader unless something says so. Focus moves to the new screen's content,
  // skipped on the first render, which is the browser's own load.
  const path = pathOf(screen);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    mainRef.current?.focus({ preventScroll: true });
  }, [path]);

  const myWork: Entry = {
    key: "queue",
    label: "My work",
    icon: "square-check",
    to: { name: "queue" },
    on: on("queue", "card"),
  };
  const notices: Entry = {
    key: "notices",
    label: "Notices",
    icon: "bell",
    to: { name: "notices" },
    on: on("notices"),
    ...(unread ? { count: unread } : {}),
  };
  const onboarding: Entry = {
    key: "onboardings",
    label: "Onboarding",
    icon: "user-plus",
    to: { name: "onboardings" },
    on: on("onboardings", "start") || (on("board", "audit") && !offboarding),
  };
  const offboardingEntry: Entry = {
    key: "offboardings",
    label: "Offboarding",
    icon: "user-minus",
    to: { name: "offboardings" },
    on: on("offboardings", "leave") || (on("board", "audit") && offboarding),
  };
  const week: Entry = {
    key: "week",
    label: "Week ahead",
    icon: "calendar",
    to: { name: "week" },
    on: on("week"),
  };
  const overdue: Entry = {
    key: "overdue",
    label: "Overdue",
    icon: "clock",
    to: { name: "overdue" },
    on: on("overdue"),
  };
  const employees: Entry = {
    key: "employees",
    label: "Employees",
    icon: "contact",
    to: { name: "employees" },
    on: on("employees", "employee"),
  };
  const log: Entry = {
    key: "log",
    label: "Audit log",
    icon: "clipboard",
    to: { name: "log" },
    on: on("log"),
  };
  const exporting: Entry = {
    key: "export",
    label: "Export data",
    icon: "inbox",
    to: { name: "export" },
    on: on("export"),
  };
  const people: Entry = {
    key: "people",
    label: "People",
    icon: "users",
    to: { name: "people" },
    on: on("people"),
  };
  const settings: Entry = {
    key: "settings",
    label: "Settings",
    icon: "settings",
    to: { name: "settings" },
    on: on("settings"),
  };
  const preview: Entry = {
    key: "preview",
    label: "Preview",
    icon: "eye",
    to: { name: "preview" },
    on: on("preview"),
  };
  const templates: Entry = {
    key: "templates",
    label: "Templates",
    icon: "workflow",
    to: { name: "templates" },
    on: on("templates", "template"),
  };
  const admin: Entry = {
    key: "admin",
    // Not an organisation's Admin role: VCNet's console over every organisation.
    label: "Platform",
    icon: "shield",
    to: { name: "admin" },
    on: on("admin"),
  };

  // What is drawn is what `mayOpen` allows, the same answer a typed address gets.
  const primary = [myWork, notices, onboarding, offboardingEntry, overdue, week, employees, log].filter(may);
  // Whoever has Templates previews from there; Preview stays for everybody else.
  const secondary = [people, settings, may(templates) ? templates : preview, exporting, admin].filter(may);
  // The phone has three slots beside Start; Notices is the top bar's bell, so it never needs one.
  const tabs = (oversees ? [myWork, onboarding, offboardingEntry] : [myWork, people, preview])
    .filter(may)
    .slice(0, 3);
  const overflow = [...primary, ...secondary].filter(
    (entry) => entry.key !== "notices" && !tabs.some((tab) => tab.key === entry.key),
  );

  const link = (entry: Entry, className: string) => (
    <Link key={entry.key} to={entry.to} className={className} current={entry.on}>
      <Icon name={entry.icon} size={20} />
      <span>{entry.label}</span>
      {entry.count ? <span className="nav-count">{entry.count} new</span> : null}
    </Link>
  );

  const identity = (
    <div className="menu-identity">
      <span className="avatar avatar-me">{initialOf(user)}</span>
      <span className="menu-identity-text">
        {user.name ? <strong>{user.name}</strong> : null}
        <span>{user.email}</span>
        {org ? <span>{org.name}</span> : null}
      </span>
    </div>
  );

  const themePicker = (
    <div className="menu-theme">
      <span>Theme</span>
      <Segmented<Theme>
        label="Theme"
        size="sm"
        value={theme}
        onChange={setTheme}
        options={[
          { value: "auto", label: <Icon name="monitor" size={16} />, title: "Match this device" },
          { value: "light", label: <Icon name="sun" size={16} />, title: "Light" },
          { value: "dark", label: <Icon name="moon" size={16} />, title: "Dark" },
        ]}
      />
    </div>
  );

  const accountItems = (close: () => void) => (
    <>
      {identity}
      {organizations.length > 1 ? (
        <label className="field menu-field">
          <span>Working in</span>
          <select
            value={org?.id ?? ""}
            onChange={(event) => {
              close();
              onOrg(event.target.value);
            }}
          >
            {organizations.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {themePicker}
      <span className="menu-divider" aria-hidden="true" />
      <MenuItem icon={<Icon name="log-out" />} disabled={leaving} onClick={() => onSignOut(false)}>
        Sign out
      </MenuItem>
      <MenuItem icon={<Icon name="log-out" />} disabled={leaving} onClick={() => onSignOut(true)}>
        Sign out everywhere
      </MenuItem>
      <p className="menu-note">Everywhere ends this account's sessions on every device.</p>
    </>
  );

  return (
    <div className="app">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>

      {/* ------------------------------------------------ sidebar, from 60rem */}
      <aside className="sidebar">
        <Link to={{ name: "queue" }} className="sidebar-brand">
          <Wordmark />
        </Link>
        {canStart ? (
          <Link to={{ name: "start" }} className="button button-block" current={on("start")}>
            <Icon name="plus" size={18} />
            Start onboarding
          </Link>
        ) : null}
        <nav className="sidebar-nav" aria-label="Main">
          {primary.map((entry) => link(entry, "nav-link"))}
          {primary.length > 0 ? <span className="nav-divider" aria-hidden="true" /> : null}
          {secondary.map((entry) => link(entry, "nav-link"))}
        </nav>
        <div className="sidebar-foot">
          <Menu
            label="Account menu"
            placement="up"
            align="start"
            buttonClassName="account-button"
            button={
              <>
                <span className="avatar avatar-me">{initialOf(user)}</span>
                <span className="account-name">
                  <span>{user.name || user.email}</span>
                  {org ? <span className="account-org">{org.name}</span> : null}
                </span>
                <Icon name="chevron-up" size={16} />
              </>
            }
          >
            {accountItems}
          </Menu>
        </div>
      </aside>

      {/* -------------------------------------------------- phone top bar */}
      <header className="topbar">
        <Link to={{ name: "queue" }} className="sidebar-brand">
          <Wordmark />
        </Link>
        {role ? (
          <Link to={{ name: "notices" }} className="icon-button topbar-bell" current={notices.on}>
            <Icon name="bell" size={20} />
            <span className="visually-hidden">{unread ? `Notices, ${unread} new` : "Notices"}</span>
            {unread ? <span className="bell-dot" aria-hidden="true" /> : null}
          </Link>
        ) : null}
      </header>

      <main id="main-content" ref={mainRef} tabIndex={-1} className="main">
        <div className="main-inner">{children}</div>
      </main>

      {/* -------------------------------------------------- phone tab bar */}
      <nav className={`tabbar${canStart ? " tabbar-five" : ""}`} aria-label="Main">
        {tabs.slice(0, 2).map((entry) => link(entry, "tab"))}
        {canStart ? (
          <Link to={{ name: "start" }} className="tab tab-new" current={on("start")}>
            <span className="tab-new-disc">
              <Icon name="plus" size={26} />
            </span>
            <span className="visually-hidden">Start onboarding</span>
          </Link>
        ) : null}
        {tabs.slice(2).map((entry) => link(entry, "tab"))}
        <Menu
          label="More"
          placement="up"
          buttonClassName={overflow.some((entry) => entry.on) ? "tab tab-on" : "tab"}
          button={
            <>
              <Icon name="menu" size={20} />
              <span>More</span>
            </>
          }
        >
          {(close) => (
            <>
              {overflow.map((entry) => (
                <Link
                  key={entry.key}
                  to={entry.to}
                  className="menu-item"
                  current={entry.on}
                  instead={() => {
                    close();
                    go(entry.to);
                  }}
                >
                  <Icon name={entry.icon} />
                  <span>{entry.label}</span>
                </Link>
              ))}
              {overflow.length > 0 ? <span className="menu-divider" aria-hidden="true" /> : null}
              {accountItems(close)}
            </>
          )}
        </Menu>
      </nav>
    </div>
  );
}
